import { Document } from '@talon/web/lib/api';
import { Anchor, Box, BoxProps, CheckBox, Text } from 'grommet';
import { DocumentUpload } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { EditableText, TalonCard } from '..';
import { useProjectStore } from '../../../context';
import { DocumentLink } from './DocumentLink';
import { DocumentUploadModal } from './DocumentUploadModal';

export type DocumentsPanelProps = {
  cardProps?: BoxProps;
  headerBackground?: string;
};

export const DocumentsPanel = observer(({ cardProps, headerBackground }: DocumentsPanelProps) => {
  const projectStore = useProjectStore();
  const [documents, setDocuments] = useState<Document[]>(projectStore.currentProject?.documents || []);
  const [, setCurrentDocument] = useState<Document | undefined>(undefined);
  const [showUpload, setShowUpload] = useState<boolean>(false);
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    const projectId = projectStore.currentProject?.id;
    if (!projectId) return;
    setDocuments(projectStore.currentProject?.documents || []);
  }, [projectStore.currentProject?.id, projectStore.currentProject?.documents]);

  const deleteDocuments = async () => {
    if (!selected.length) return;
    for (const id of selected) {
      await projectStore.deleteDocument(id);
    }
    setDocuments(documents.filter((l) => !selected.includes(l.id)));
    setCurrentDocument(undefined);
    setSelected([]);
  };

  const updateDocument = async (update: Document) => {
    const document = await projectStore.updateDocument(update.id, update);
    if (!document) return;
    const newDocuments = documents.map((d) => (d.id === document.id ? document : d));
    setDocuments(newDocuments);
  };

  const newDoc = () => {
    setShowUpload(true);
  };

  const choose = (id: string) => {
    if (selected.includes(id)) {
      setSelected(selected.filter((s) => s !== id));
      setCurrentDocument(undefined);
    } else {
      setSelected([...selected, id]);
      setCurrentDocument(documents.find((d) => d.id === id));
    }
  };
  const selectedNames = () => {
    return selected.map((s) => documents.find((item) => item.id === s)?.name).join(', ');
  };

  return (
    <TalonCard
      newItem={newDoc}
      deleteItems={deleteDocuments}
      label="Document"
      labelPlural="Documents"
      addIcon={<DocumentUpload />}
      selectedNames={selectedNames()}
      cardProps={cardProps}
      headerBackground={headerBackground}
    >
      {showUpload && <DocumentUploadModal setShowUpload={setShowUpload} />}
      <Box direction="column" overflow={{ vertical: 'auto' }} align="start" gap="small" pad="small">
        {documents.map((doc) => (
          <Box direction="row" align="center" gap="small" key={doc.id}>
            <CheckBox checked={selected.includes(doc.id)} onChange={() => choose(doc.id)} />
            <Anchor onClick={() => setCurrentDocument(doc)}>
              <DocumentLink doc={doc} />
            </Anchor>
            <Text size="xsmall" color="dark-5">
              {doc.type}
            </Text>
            <EditableText val={doc.name} onChange={(name) => updateDocument({ ...doc, name })} />
            <Text size="xsmall" color="dark-5">
              {new Date(doc.created_at).toLocaleString()}
            </Text>
          </Box>
        ))}
      </Box>
    </TalonCard>
  );
});
